import { Skeleton } from "./ui/skeleton";
import { Card, CardContent, CardHeader } from "./ui/card";

/**
 * Skeleton loaders pentru Dashboard.
 * Respectă layout-ul final (acelaș grid + dimensiuni)
 * → zero layout shift când datele sosesc.
 */

export const StatCardSkeleton = ({ accent = "bg-slate-200" }) => (
  <Card className={`border-l-4 ${accent}`}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-20" />
        </div>
        <Skeleton className="h-14 w-14 rounded-full" />
      </div>
      <Skeleton className="h-4 w-24 mt-3" />
    </CardContent>
  </Card>
);

export const TopListSkeleton = ({ rows = 5, title = true }) => (
  <Card>
    {title && (
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-44 mt-2" />
      </CardHeader>
    )}
    <CardContent className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <Skeleton className="h-6 w-10" />
        </div>
      ))}
    </CardContent>
  </Card>
);

export const StatusBreakdownSkeleton = ({ rows = 4 }) => (
  <Card>
    <CardHeader className="pb-3">
      <Skeleton className="h-5 w-40" />
    </CardHeader>
    <CardContent className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-5 w-10" />
        </div>
      ))}
    </CardContent>
  </Card>
);

export const DashboardSkeleton = () => (
  <div className="space-y-8 animate-fade-in" data-testid="dashboard-loading">
    {/* Header */}
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-52" />
      </div>
      <div className="flex gap-3">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-36" />
      </div>
    </div>

    {/* KPI cards — same grid as final layout */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCardSkeleton accent="border-l-[#1e3a8a]" />
      <StatCardSkeleton accent="border-l-emerald-500" />
      <StatCardSkeleton accent="border-l-blue-500" />
      <StatCardSkeleton accent="border-l-orange-500" />
    </div>

    {/* Top widgets row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TopListSkeleton rows={5} />
      <TopListSkeleton rows={5} />
    </div>

    {/* Status breakdown + recent imports row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <StatusBreakdownSkeleton rows={6} />
      <TopListSkeleton rows={5} />
    </div>
  </div>
);

/**
 * Skeleton pentru tabele paginate (Sales / Contracts).
 * Imitează structura: header coloane + N rânduri.
 */
export const TableSkeleton = ({ rows = 8, columns = 8 }) => (
  <div className="overflow-x-auto" data-testid="table-loading">
    <table className="w-full">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton className="h-3 w-20" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: columns }).map((_, c) => (
              <td key={c} className="px-4 py-4">
                <Skeleton className={`h-4 ${c === 0 ? "w-6" : c === columns - 1 ? "w-16" : "w-3/4"}`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
